import {
  siteCreateModelToSiteCreateOutDto,
  siteEditModelToSiteChangeOutDto,
} from 'shared/domain/site/mapping/toOutDto';
import { SiteInDto } from 'shared/dtos/in/site';
import {
  ISiteUploader,
  UploadSite,
  UploadSiteChanges,
  UploadSiteDelete,
  UploadSiteRestore,
} from 'serviceWorker/repository/site/uploadSite';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';
import {
  SiteHttpCreateRequestModel,
  SiteHttpDeleteRequestModel,
  SiteHttpEditRequestModel,
  SiteHttpRestoreRequestModel,
} from './types';

export class SiteRequestRunner implements IHttpRequestRunner {
  constructor(
    private siteUploader: ISiteUploader,
    private uploadSiteUseCase: UploadSite,
    private saveSite: (data: SiteInDto) => Promise<unknown>,
    private uploadSiteChangesUseCase: UploadSiteChanges,
    private uploadSiteDeleteUseCase: UploadSiteDelete,
    private deleteSite: (siteId: string) => Promise<unknown>,
    private uploadSiteRestoreUseCase: UploadSiteRestore,
    private restoreSite: (siteId: string) => Promise<unknown>
  ) {}
  async execute(
    request:
      | SiteHttpCreateRequestModel
      | SiteHttpEditRequestModel
      | SiteHttpDeleteRequestModel
      | SiteHttpRestoreRequestModel
  ): Promise<boolean> {
    switch (request.method) {
      case 'POST': {
        return this.uploadSite(request);
      }
      case 'PUT': {
        return this.uploadSiteChanges(request);
      }
      case 'DELETE': {
        return this.uploadSiteDelete(
          request
        );
      }
      case 'GET': {
        return this.uploadSiteRestore(
          request
        );
      }
    }
  }

  private async uploadSite(
    request: SiteHttpCreateRequestModel
  ): Promise<boolean> {
    if (!request.data?.siteCreateModel) {
      throw new Error('Cannot upload site without values');
    }
    const url = `/project/${request.data.siteCreateModel.projectId}/site`;
    const siteOutDto = siteCreateModelToSiteCreateOutDto(
      request.data.siteCreateModel
    );

    const siteInDto = await this.uploadSiteUseCase(
      this.siteUploader,
      siteOutDto,
      url
    );
    await this.saveSite(siteInDto);
    return true;
  }

  private async uploadSiteChanges(
    request: SiteHttpEditRequestModel
  ): Promise<boolean> {
    if (!request.data.siteEditModel) {
      throw new Error('Cannot update site without values');
    }
    const siteOutDto = siteEditModelToSiteChangeOutDto(
      request.data.siteEditModel
    );
    // Stored, so the page reads the edited site (and the next edit starts from it).
    const siteInDto = await this.uploadSiteChangesUseCase(
      this.siteUploader,
      siteOutDto,
      request.url
    );
    await this.saveSite(siteInDto);
    return true;
  }

  private async uploadSiteDelete(
    request: SiteHttpDeleteRequestModel
  ): Promise<boolean> {
    await this.uploadSiteDeleteUseCase(this.siteUploader, request.url);
    await this.deleteSite(request.data.siteId);
    return true;
  }

  private async uploadSiteRestore(
    request: SiteHttpRestoreRequestModel
  ): Promise<boolean> {
    const url = `/project/${request.data.projectId}/site/${request.data.siteId}/restore`;

    await this.uploadSiteRestoreUseCase(this.siteUploader, url);
    await this.restoreSite(request.data.siteId);
    return true;
  }
}
